/**
 * The Completed-orders review lane (production-records R0). Every worklist drops an order the moment
 * it finishes; this view is the way back — find any finished order by number, garment, or SKU, see
 * its derived production state at a glance (a "Shipped" order that was never marked printed shows
 * exactly that), and jump to its job card. Read-only: the server returns the full terminal set (the
 * order book is small), search and store filtering happen client-side.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from '@tanstack/react-router';
import { Archive, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { Skeleton } from '@/components/ui/skeleton';
import { useApiClient } from '@/lib/api/hooks';
import type { components } from '@/lib/api/types.gen';
import {
  GARMENT_TONE_CLASS,
  type GarmentTone,
  orderCardSearch,
  PRODUCTION_STATUS_LABEL,
} from './order-detail-lib';
import { relativeAge } from './orders-queue';

export type CompletedOrder = components['schemas']['CompletedOrderView'];

/**
 * Tone for the production badge in a TERMINAL-orders list. Local on purpose: the lib's status tone
 * stays private behind `orderHeadline` (the D4 ship-claim gate), and no ship decision exists here —
 * every order in this list already shipped or was cancelled. This badge is a historical record.
 */
export function completedStatusTone(status: CompletedOrder['production']['status']): GarmentTone {
  switch (status) {
    case 'PRINTED':
    case 'PRODUCED':
    case 'PACKED':
    case 'SHIPPED':
      return 'done';
    case 'NEEDS_ACTION':
      return 'attention';
    case 'IN_PRODUCTION':
    case 'NOT_STARTED':
      return 'progress';
    default:
      return 'muted';
  }
}

/** Case-insensitive substring match over order #, garment titles, and SKUs. */
export function matchesCompletedSearch(order: CompletedOrder, term: string): boolean {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  if (order.orderName.toLowerCase().includes(q)) return true;
  if (order.titles.some((t) => t.toLowerCase().includes(q))) return true;
  return order.skus.some((s) => s.toLowerCase().includes(q));
}

export function CompletedOrdersView() {
  const client = useApiClient();
  const [search, setSearch] = useState('');
  const [store, setStore] = useState<string>('all');

  const query = useQuery({
    queryKey: ['orders', 'completed'],
    queryFn: async () => {
      const { data, error } = await client.GET('/orders/completed', {});
      if (error) throw new Error(error?.error?.detail ?? 'Failed to load completed orders.');
      if (!data?.ok) throw new Error('Empty response from the server.');
      return data.data;
    },
  });

  const items = useMemo(() => query.data?.items ?? [], [query.data]);
  const storeOptions = useMemo(() => [...new Set(items.map((o) => o.storeId))].sort(), [items]);
  const visible = items.filter(
    (o) => (store === 'all' || o.storeId === store) && matchesCompletedSearch(o, search),
  );
  const nowMs = Date.now();

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-1/2 size-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search order #, garment, or SKU…"
            className="pl-8"
            aria-label="Search completed orders"
          />
        </div>
        {storeOptions.length > 1 && (
          <Select value={store} onValueChange={setStore}>
            <SelectTrigger className="w-40">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All stores</SelectItem>
              {storeOptions.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
        {!query.isLoading && (
          <span className="text-sm text-muted-foreground">
            {visible.length} of {items.length} finished orders
          </span>
        )}
      </div>

      {query.isError && (
        <Alert variant="destructive">
          <AlertTitle>Couldn’t load completed orders</AlertTitle>
          <AlertDescription>
            {query.error instanceof Error ? query.error.message : 'Please try again.'}
          </AlertDescription>
        </Alert>
      )}

      {query.isLoading ? (
        <div className="space-y-2">
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
          <Skeleton className="h-16 w-full" />
        </div>
      ) : visible.length === 0 ? (
        <div className="flex flex-col items-center gap-2 rounded-lg border border-dashed py-16 text-center text-muted-foreground">
          <Archive className="size-8" />
          <p className="text-sm">
            {items.length === 0 ? 'No finished orders yet.' : 'Nothing matches that search.'}
          </p>
        </div>
      ) : (
        <ul className="divide-y rounded-lg border">
          {visible.map((o) => {
            const preview = o.titles.slice(0, 2).join(', ');
            const more = o.titles.length > 2 ? ` +${o.titles.length - 2} more` : '';
            return (
              <li key={`${o.storeId}#${o.orderId}`}>
                <Link
                  to="/orders/$storeId/$orderId"
                  params={{ storeId: o.storeId, orderId: o.orderId }}
                  search={orderCardSearch('orders-completed')}
                  className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 px-4 py-3 hover:bg-muted/40"
                >
                  <div className="min-w-0">
                    <p className="flex flex-wrap items-center gap-2 text-sm font-medium">
                      {o.orderName}
                      <span className="rounded bg-muted px-1.5 py-0.5 text-xs font-normal text-muted-foreground">
                        {o.storeId}
                      </span>
                      {o.fulfillmentState === 'CANCELLED' && (
                        <Badge variant="outline" className={GARMENT_TONE_CLASS.muted}>
                          Cancelled
                        </Badge>
                      )}
                    </p>
                    <p className="truncate text-xs text-muted-foreground">
                      {o.lineCount} garment{o.lineCount === 1 ? '' : 's'}
                      {preview ? ` · ${preview}${more}` : ''}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    {/* THE review signal: the order shipped, but some of its production was never
                        recorded in here (unproduced cap, unpacked ship-only, unprinted DTF). The
                        rollup can't say this — fulfillment outranks workflow in its status. */}
                    {o.unrecordedLines > 0 && (
                      <Badge
                        variant="outline"
                        className={GARMENT_TONE_CLASS.attention}
                        title="Shipped before production was recorded — open the order to record it."
                      >
                        {o.unrecordedLines} not recorded
                      </Badge>
                    )}
                    <Badge
                      variant="outline"
                      className={GARMENT_TONE_CLASS[completedStatusTone(o.production.status)]}
                    >
                      {PRODUCTION_STATUS_LABEL[o.production.status]}
                    </Badge>
                    {/* The observed ship moment (R2) when stamped — the lane sorts by it; the
                        pre-R2 book has none and falls back to the order's age. */}
                    {o.fulfilledAt ? (
                      <span
                        className="text-xs text-muted-foreground"
                        title={new Date(o.fulfilledAt).toLocaleString()}
                      >
                        shipped {relativeAge(o.fulfilledAt, nowMs)}
                      </span>
                    ) : (
                      <span
                        className="text-xs text-muted-foreground"
                        title={new Date(o.shopifyCreatedAt).toLocaleString()}
                      >
                        {relativeAge(o.shopifyCreatedAt, nowMs)}
                      </span>
                    )}
                  </div>
                </Link>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
